/**
 * KRA Photo Journal – Admin Panel
 *
 * Shown only when currentUser === "Admin".
 * Features:
 *   - Generate temporary access links (30 / 60 / 90 minutes)
 *     The link bypasses IP/GPS check so photographers can log in from anywhere.
 */

import React, { useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  useGoogleDrive,
  extractFolderIdFromUrl,
  getFolderWithParentName,
} from "../hooks/useGoogleDrive.js";
import { patchPhoto, fetchPhotos } from "../lib/api.js";
import { runLinkScan, type LinkScanProgress } from "../lib/linkByName.js";
import { readQuickFolder, clearQuickFolder } from "../lib/quick-folder.js";
import { folderUrl } from "../hooks/useGoogleDrive.js";

const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api";
const TOKEN_KEY = "kra_token";
const APP_URL = window.location.origin;

interface AdminPanelProps {
  onClose: () => void;
  /** Admin-only tools (Drive folder backfill) are hidden for everyone else. */
  isAdmin?: boolean;
  /** Called after the backfill writes folders, so the pills can rescan. */
  onFoldersChanged?: () => void;
}

type BackfillPhase = "idle" | "scanning" | "running";

interface BackfillState {
  phase: BackfillPhase;
  done: number;
  total: number;
  errors: number;
  message: string | null;
}

/** Sets fetched per scan page, and the ceiling on one scan batch before we
 *  start writing. The run rescans afterwards, so this only caps batch size. */
const SCAN_PAGE_SIZE = 100;
const MAX_PER_RUN = 2000;
/** Airtable allows ~5 writes/s per base — stay well under it. */
const WRITE_DELAY_MS = 220;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

type Duration = 30 | 60 | 90;

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function formatExpiry(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
}

export function AdminPanel({ onClose, isAdmin, onFoldersChanged }: AdminPanelProps) {
  const [duration, setDuration] = useState<Duration>(60);
  const [generatedURL, setGeneratedURL] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [quickFolder, setQuickFolder] = useState(() => readQuickFolder());

  // ── Backfill "Admin_category_folder" for existing records ──────────────────
  // Targets come from the server (`missingFolder=1`), not the loaded feed —
  // the feed only holds one page at a time. Interruptible: whatever was
  // written stays written, and the next run picks up what's still blank.
  const { requestToken } = useGoogleDrive();
  const [backfill, setBackfill] = useState<BackfillState>({
    phase: "idle", done: 0, total: 0, errors: 0, message: null,
  });
  const stopRef = useRef(false);

  const handleBackfill = useCallback(async () => {
    stopRef.current = false;

    let token: string;
    try {
      token = await requestToken();
    } catch (e) {
      setBackfill({
        phase: "idle", done: 0, total: 0, errors: 0,
        message: e instanceof Error ? e.message : "Lỗi đăng nhập Google",
      });
      return;
    }

    // Many sets share the same parent — resolve each parent's name once.
    const parentNameCache = new Map<string, string | null>();
    // Every record tried this run. Airtable's paginated scan hands back only a
    // few hundred rows at a time, so we scan → write → rescan until nothing new
    // shows up; without this set the sets that fail would come straight back.
    const attempted = new Set<string>();
    let done = 0;
    let errors = 0;
    let scanned = 0;

    while (!stopRef.current) {
      // ── 1. Collect the sets still missing a category folder ───────────────
      setBackfill((s) => ({ ...s, phase: "scanning", message: null }));
      const targets: Array<{ id: string; adminDriveLink: string }> = [];
      try {
        let offset: string | undefined;
        do {
          const page = await fetchPhotos({
            missingFolder: true,
            pageSize: SCAN_PAGE_SIZE,
            ...(offset ? { offset } : {}),
          });
          for (const rec of page.records) {
            if (rec.adminDriveLink?.trim() && !attempted.has(rec.id)) {
              targets.push({ id: rec.id, adminDriveLink: rec.adminDriveLink });
            }
          }
          setBackfill((s) => ({ ...s, total: scanned + targets.length }));
          offset = page.offset ?? undefined;
        } while (offset && targets.length < MAX_PER_RUN && !stopRef.current);
      } catch (e) {
        setBackfill((s) => ({
          ...s, phase: "idle",
          message: e instanceof Error ? e.message : "Không quét được danh sách bộ ảnh",
        }));
        return;
      }

      // Nothing new left to try — either all done, or only failures remain.
      if (targets.length === 0) break;

      scanned += targets.length;

      // ── 2. Resolve each set's parent folder on Drive, then write it ───────
      setBackfill((s) => ({ ...s, phase: "running", total: scanned }));

      for (const t of targets) {
        if (stopRef.current) break;
        attempted.add(t.id);

        const folderId = extractFolderIdFromUrl(t.adminDriveLink);
        if (!folderId) {
          errors++;
          setBackfill((s) => ({ ...s, errors }));
          continue;
        }

        try {
          const meta = await getFolderWithParentName(folderId, token);
          const cached = meta.parentId ? parentNameCache.get(meta.parentId) : undefined;
          const category = (cached ?? meta.parentName)?.trim();
          if (meta.parentId && cached === undefined) {
            parentNameCache.set(meta.parentId, meta.parentName);
          }

          if (category) {
            await patchPhoto(t.id, { adminCategoryFolder: category });
            done++;
          } else {
            errors++;
          }
        } catch {
          errors++;
        }

        setBackfill((s) => ({ ...s, done, errors }));
        await sleep(WRITE_DELAY_MS);
      }
    }

    if (scanned === 0) {
      setBackfill({
        phase: "idle", done: 0, total: 0, errors: 0,
        message: "Không có bộ nào cần điền — tất cả đã có folder.",
      });
      return;
    }

    const stopped = stopRef.current;
    setBackfill({
      phase: "idle", done, total: scanned, errors,
      message: stopped
        ? `Đã dừng ở ${done}/${scanned}. Bấm lại để làm tiếp phần còn lại.`
        : `Xong: điền ${done} bộ${errors ? `, ${errors} bộ không lấy được folder mẹ` : ""}.`,
    });

    if (done > 0) onFoldersChanged?.();
  }, [requestToken, onFoldersChanged]);

  // ── Nối bộ cũ với bản copy trên Drive (quét ngược theo tên) ───────────────
  // Backfill ở trên chỉ chạm được bộ đã có link Drive admin. Phần này đi từ
  // Drive về: lập chỉ mục cây "01. Kimono" rồi khớp tên folder với tên bộ.
  const [linkScan, setLinkScan] = useState<LinkScanProgress | null>(null);
  const [linkMessage, setLinkMessage] = useState<string | null>(null);
  const linkStopRef = useRef(false);

  const handleLinkScan = useCallback(async () => {
    if (linkScan) { linkStopRef.current = true; return; }
    linkStopRef.current = false;
    setLinkMessage(null);

    let token: string;
    try {
      token = await requestToken();
    } catch (e) {
      setLinkMessage(e instanceof Error ? e.message : "Lỗi đăng nhập Google");
      return;
    }

    try {
      const res = await runLinkScan({
        token,
        onProgress: setLinkScan,
        shouldStop: () => linkStopRef.current,
      });
      setLinkMessage(
        `${res.stopped ? "Đã dừng" : "Xong"}: nối ${res.written} bộ vào folder trên Drive` +
        `${res.unmatched ? `, ${res.unmatched} bộ không tìm thấy folder trùng tên` : ""}.`,
      );
      if (res.written > 0) onFoldersChanged?.();
    } catch (e) {
      setLinkMessage(e instanceof Error ? e.message : "Quét Drive thất bại");
    } finally {
      setLinkScan(null);
    }
  }, [linkScan, requestToken, onFoldersChanged]);

  const handleGenerate = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setGeneratedURL(null);

    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE}/auth/create-temp-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ minutes: duration }),
      });

      const data = (await resp.json()) as {
        token?: string;
        expiresAt?: string;
        minutes?: number;
        error?: string;
      };

      if (!resp.ok || !data.token) {
        setError(data.error ?? "Không thể tạo link");
        return;
      }

      const url = `${APP_URL}/?access=${encodeURIComponent(data.token)}`;
      setGeneratedURL(url);
      setExpiresAt(data.expiresAt ?? null);
    } catch {
      setError("Lỗi kết nối, thử lại nhé");
    } finally {
      setIsLoading(false);
    }
  }, [duration]);

  const handleCopy = useCallback(() => {
    if (!generatedURL) return;
    navigator.clipboard.writeText(generatedURL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [generatedURL]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="w-full max-w-sm bg-[#1c1c1e] rounded-2xl overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div>
            <h2 className="text-white text-sm font-semibold">🔗 Tạo link tạm thời</h2>
            <p className="text-white/40 text-xs mt-0.5">
              Link bỏ qua kiểm tra vị trí, dùng được từ bất kỳ đâu
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 text-white/60 hover:text-white hover:bg-white/15 transition-colors"
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Duration selector */}
        <div className="px-5 pt-5 pb-4">
          <p className="text-white/60 text-xs mb-3">Thời hạn</p>
          <div className="flex gap-2">
            {([30, 60, 90] as Duration[]).map((m) => (
              <button
                key={m}
                onClick={() => { setDuration(m); setGeneratedURL(null); }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all duration-150
                  ${duration === m
                    ? "bg-blue-500 text-white"
                    : "bg-white/10 text-white/60 hover:bg-white/15 hover:text-white"
                  }`}
              >
                {m} phút
              </button>
            ))}
          </div>

          <button
            onClick={handleGenerate}
            disabled={isLoading}
            className="mt-4 w-full py-3 rounded-xl bg-blue-500 text-white text-sm font-medium
              hover:bg-blue-400 active:scale-[0.98] transition-all duration-150
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Đang tạo…" : "Tạo link"}
          </button>

          {error && (
            <p className="text-red-400 text-xs text-center mt-2">{error}</p>
          )}
        </div>

        {/* Generated URL */}
        <AnimatePresence>
          {generatedURL && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-5">
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-white/40 text-2xs mb-1 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                        Hết hạn lúc {expiresAt ? formatExpiry(expiresAt) : "—"}
                      </p>
                      <p className="text-white/80 text-xs break-all leading-relaxed font-mono">
                        {generatedURL}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleCopy}
                    className={`mt-3 w-full py-2 rounded-lg text-xs font-medium transition-all duration-150
                      ${copied
                        ? "bg-green-500/20 text-green-400"
                        : "bg-white/10 text-white/70 hover:bg-white/15 hover:text-white"
                      }`}
                  >
                    {copied ? "✓ Đã sao chép!" : "Sao chép link"}
                  </button>
                </div>

                <p className="text-white/30 text-2xs text-center mt-2 leading-relaxed">
                  Gửi link này cho thợ ảnh. Sau khi mở link họ vẫn cần<br />
                  chọn tên và nhập mã PIN để vào app.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── ⚡ Import nhanh destination ─────────────────────────────────── */}
        {isAdmin && (
          <div className="px-5 pt-4 pb-5 border-t border-white/8">
            <h2 className="text-white text-sm font-semibold">⚡ Import nhanh lưu vào</h2>
            <p className="text-white/40 text-xs mt-0.5 leading-relaxed">
              Nút ⚡ trên thẻ copy bộ ảnh vào folder này trên Drive của anh (bản gốc
              của thợ vẫn giữ). Mỗi máy/trình duyệt nhớ folder riêng.
            </p>
            {quickFolder ? (
              <div className="mt-3 flex items-center gap-2">
                <a
                  href={folderUrl(quickFolder.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="min-w-0 flex-1 truncate rounded-xl bg-white/10 px-3 py-2.5 text-sm text-white hover:bg-white/15"
                >
                  📁 {quickFolder.name}
                </a>
                <button
                  onClick={() => {
                    clearQuickFolder();
                    setQuickFolder(null);
                  }}
                  className="flex-shrink-0 rounded-xl bg-white/10 px-3 py-2.5 text-sm text-white hover:bg-white/15"
                >
                  Chọn lại
                </button>
              </div>
            ) : (
              <p className="mt-3 rounded-xl bg-white/5 px-3 py-2.5 text-xs text-white/60">
                Chưa chọn — lần bấm ⚡ tới, app sẽ hỏi folder rồi nhớ luôn.
              </p>
            )}
          </div>
        )}

        {/* ── Backfill Drive folder (category) ─────────────────────────────── */}
        {isAdmin && (
          <div className="px-5 pt-4 pb-5 border-t border-white/8">
            <h2 className="text-white text-sm font-semibold">📁 Điền folder Drive cho bộ cũ</h2>
            <p className="text-white/40 text-xs mt-0.5 leading-relaxed">
              Đọc folder mẹ (Couples, Furisode cao cấp 2…) của các bộ đã copy vào Drive
              để lọc theo folder. Đăng nhập Google 1 lần, sau đó lưu sẵn — không cần lại.
              Chạy lâu thì cứ dừng, lần sau bấm tiếp phần còn lại.
            </p>

            <button
              onClick={() => {
                if (backfill.phase === "idle") void handleBackfill();
                else stopRef.current = true;
              }}
              className="mt-3 w-full py-3 rounded-xl bg-[#B76E6E] text-white text-sm font-medium
                hover:bg-[#a85f5f] active:scale-[0.98] transition-all duration-150"
            >
              {backfill.phase === "scanning"
                ? `Đang tìm bộ cần điền… ${backfill.total} — Dừng`
                : backfill.phase === "running"
                  ? `Đang điền… ${backfill.done}/${backfill.total} — Dừng`
                  : "Điền folder cho bộ cũ"}
            </button>

            {backfill.phase === "running" && backfill.total > 0 && (
              <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-[#B76E6E] transition-all duration-300"
                  style={{ width: `${Math.round((backfill.done / backfill.total) * 100)}%` }}
                />
              </div>
            )}

            {backfill.message && (
              <p className="text-white/60 text-xs text-center mt-2 leading-relaxed">
                {backfill.message}
              </p>
            )}
          </div>
        )}

        {/* ── Nối bộ cũ với folder trên Drive (quét ngược theo tên) ────────── */}
        {isAdmin && (
          <div className="px-5 pt-4 pb-5 border-t border-white/8">
            <h2 className="text-white text-sm font-semibold">🔗 Nối bộ cũ với folder trên Drive</h2>
            <p className="text-white/40 text-xs mt-0.5 leading-relaxed">
              Dành cho bộ anh copy vào Drive bằng tay nên app không biết nằm đâu.
              Quét cây 01. Kimono rồi khớp tên folder với tên bộ; khớp thì lưu cả
              link Drive lẫn folder. Chỉ khớp tên trùng khít, không đoán mò.
            </p>

            <button
              onClick={() => { void handleLinkScan(); }}
              className="mt-3 w-full py-3 rounded-xl bg-white/10 text-white text-sm font-medium
                hover:bg-white/15 active:scale-[0.98] transition-all duration-150"
            >
              {linkScan === null
                ? "Quét Drive nối bộ cũ"
                : linkScan.phase === "root"
                  ? "Đang dò cây thư mục… — Dừng"
                  : linkScan.phase === "index"
                    ? `Đang đọc Drive… ${linkScan.indexed} folder — Dừng`
                    : linkScan.phase === "scan"
                      ? `Đang tìm bộ chưa nối… ${linkScan.candidates} — Dừng`
                      : `Đang nối… ${linkScan.written}/${linkScan.candidates} — Dừng`}
            </button>

            {linkScan?.phase === "write" && linkScan.candidates > 0 && (
              <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-white/40 transition-all duration-300"
                  style={{
                    width: `${Math.round(
                      ((linkScan.written + linkScan.unmatched) / linkScan.candidates) * 100,
                    )}%`,
                  }}
                />
              </div>
            )}

            {linkMessage && (
              <p className="text-white/60 text-xs text-center mt-2 leading-relaxed">
                {linkMessage}
              </p>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
